import { Outlet } from "react-router";
import {
  Search,
  Home,
  Users,
  Contact,
  DollarSign,
  Settings,
  LogOut,
  Trash,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronRight,
  ChevronsUpDown,
  Calendar,
  WavesLadder,
  BarChart3,
  BanknoteX,
  AlertTriangle,
  Archive,
  type LucideIcon,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { SeedPopover } from "@/components/seed-popover";
import { SentryTestButton } from "@/components/sentry-test-button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { authClient } from "@/lib/auth-client";
import { useNavigate, useLocation } from "react-router";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { OrgSelection } from "@/components/org-selection";
import { useOrgSelection } from "@/hooks/useOrgSelection";
import { useState, useCallback, useEffect, useRef } from "react";
import { type OnboardingStatusEnum } from "nautika-types";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

// ============================================================================
// TYPES & CONFIGURATION
// ============================================================================

/** Action button configuration for a navigation item */
interface NavAction {
  icon: LucideIcon;
  tooltip: string;
  path: string;
}

/** Sub-page/child item configuration */
interface NavSubItem {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  enabled: boolean;
}

/** Main navigation item configuration */
interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  enabled: boolean;
  /** Sub-items make this item collapsible */
  children?: NavSubItem[];
  /** Optional action button (e.g., "+" to create) */
  action?: NavAction;
  /** Path prefix to match for "active" state (defaults to path) */
  activePathPrefix?: string;
  /** If true, only exact path match (no prefix matching) */
  exactMatch?: boolean;
}

/**
 * Navigation configuration - define all sidebar items here
 * This is the single source of truth for sidebar navigation
 */
const navigationConfig: NavItem[] = [
  {
    id: "overview",
    label: "Vue d'ensemble",
    path: "/dash",
    icon: Home,
    enabled: true,
    exactMatch: true, // Only match /dash exactly, not /dash/registrations etc.
    children: [
      { id: "planning-club", label: "Planning du club", path: "/dash?tab=planning-club", icon: Calendar, enabled: true },
      { id: "planning-natation", label: "Planning de la natation", path: "/dash?tab=planning-natation", icon: WavesLadder, enabled: false },
      { id: "statistiques", label: "Statistiques", path: "/dash?tab=statistiques", icon: BarChart3, enabled: false },
    ],
  },
  {
    id: "registrations",
    label: "Inscriptions",
    path: "/dash/registrations",
    icon: Search,
    enabled: true,
    activePathPrefix: "/dash/registrations",
    // action: {
    //   icon: Plus,
    //   tooltip: "Nouvelle inscription",
    //   path: "/dash/registrations/new",
    // },
    children: [
      { id: "all", label: "Toutes les inscriptions", path: "/dash/registrations?tab=all", icon: Users, enabled: true },
      { id: "pending", label: "En attente de règlement", path: "/dash/registrations?tab=pending", icon: BanknoteX, enabled: true },
      { id: "incomplete", label: "Inscriptions à compléter", path: "/dash/registrations?tab=incomplete", icon: AlertTriangle, enabled: true },
      { id: "archived", label: "Archivées", path: "/dash/registrations?tab=archived", icon: Archive, enabled: true },
    ],
  },
  {
    id: "contacts",
    label: "Contacts",
    path: "/dash/contacts",
    icon: Contact,
    enabled: false,
  },
  {
    id: "animateurs",
    label: "Animateurs",
    path: "/dash/animateurs",
    icon: Users,
    enabled: false,
  },
  {
    id: "comptabilite",
    label: "Comptabilité",
    path: "/dash/comptabilite",
    icon: DollarSign,
    enabled: false,
  },
  {
    id: "settings",
    label: "Paramètres",
    path: "/dash/settings",
    icon: Settings,
    enabled: true,
  },
];

// ============================================================================
// CONSTANTS
// ============================================================================

const MIN_WIDTH = 56;
const MAX_WIDTH = 320;
const COLLAPSE_THRESHOLD = 140;
const DEFAULT_WIDTH = 240;

const onboardingSteps: { value: OnboardingStatusEnum; label: string }[] = [
  { value: "step2", label: "2 - Vérification du mail" },
  { value: "step3", label: "3 - Informations du club" },
  { value: "step4", label: "4 - Périodes d'ouvertures" },
  { value: "step6", label: "6 - Périodes de fermetures" },
  { value: "step7", label: "7 - Groupes d'âges" },
  { value: "step8", label: "8 - Abonnements" },
  { value: "done", label: "Terminé" },
];

// ============================================================================
// SIDEBAR COMPONENT
// ============================================================================

export function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: session } = authClient.useSession();

  // État pour gérer le changement d'étape d'onboarding
  const [isChangingOnboarding, setIsChangingOnboarding] = useState(false);

  // Dynamic open states for collapsible items
  const [openStates, setOpenStates] = useState<Record<string, boolean>>(() => {
    // Initialize open states based on current path
    const states: Record<string, boolean> = {};
    navigationConfig.forEach((item) => {
      if (item.children) {
        const prefix = item.activePathPrefix || item.path;
        states[item.id] = location.pathname === prefix || location.pathname.startsWith(prefix);
      }
    });
    return states;
  });

  // Refs to save open states before collapse
  const savedOpenStates = useRef<Record<string, boolean>>({});

  // État pour le collapse et le resize
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isDragCollapsed, setIsDragCollapsed] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(DEFAULT_WIDTH);
  const [isDragging, setIsDragging] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const widthRef = useRef(DEFAULT_WIDTH);
  const previousWidthRef = useRef(DEFAULT_WIDTH);
  const wasDragCollapsedRef = useRef(false);

  // Hook pour la gestion des organisations
  const { organizations, activeOrg, showOrgSelection, selectedOrgId, setSelectedOrgId, isSettingActive, handleSetActiveOrganization, onCloseOrgSelection } =
    useOrgSelection();

  // ============================================================================
  // RESIZE & COLLAPSE HANDLERS
  // ============================================================================

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      widthRef.current = sidebarWidth;
      setIsDragging(true);
    },
    [sidebarWidth]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !sidebarRef.current) return;

      const newWidth = e.clientX;
      const shouldCollapse = newWidth < COLLAPSE_THRESHOLD;
      const wasCollapsed = wasDragCollapsedRef.current;

      if (shouldCollapse) {
        widthRef.current = MIN_WIDTH;
        sidebarRef.current.style.width = `${MIN_WIDTH}px`;
        sidebarRef.current.dataset.collapsed = "true";

        if (!wasCollapsed) {
          savedOpenStates.current = { ...openStates };
          wasDragCollapsedRef.current = true;
          setIsDragCollapsed(true);
        }
      } else {
        const clampedWidth = Math.min(Math.max(newWidth, MIN_WIDTH), MAX_WIDTH);
        widthRef.current = clampedWidth;
        sidebarRef.current.style.width = `${clampedWidth}px`;
        sidebarRef.current.dataset.collapsed = "false";

        if (wasCollapsed) {
          wasDragCollapsedRef.current = false;
          setIsDragCollapsed(false);
          setOpenStates(savedOpenStates.current);
        }
      }
    },
    [isDragging, openStates]
  );

  const handleMouseUp = useCallback(() => {
    const finalWidth = widthRef.current;
    const finalCollapsed = finalWidth < COLLAPSE_THRESHOLD;
    setIsCollapsed(finalCollapsed);
    setIsDragCollapsed(false);
    wasDragCollapsedRef.current = false;
    setSidebarWidth(finalCollapsed ? MIN_WIDTH : finalWidth);
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const toggleCollapse = useCallback(() => {
    if (isCollapsed) {
      setIsCollapsed(false);
      setSidebarWidth(previousWidthRef.current);
      if (sidebarRef.current) {
        sidebarRef.current.dataset.collapsed = "false";
      }
    } else {
      previousWidthRef.current = sidebarWidth;
      setIsCollapsed(true);
      setSidebarWidth(MIN_WIDTH);
      if (sidebarRef.current) {
        sidebarRef.current.dataset.collapsed = "true";
      }
    }
  }, [isCollapsed, sidebarWidth]);

  // ============================================================================
  // AUTH & USER HANDLERS
  // ============================================================================

  const handleOnboardingStepChange = async (newStep: OnboardingStatusEnum) => {
    if (newStep === session?.user?.onboarding) return;

    setIsChangingOnboarding(true);
    try {
      await authClient.updateUser({ onboarding: newStep });
      toast.success(`Redirection vers l'étape ${newStep}`);
      if (newStep === "done") {
        navigate("/dash");
      } else {
        navigate(`/onboarding/${newStep}`);
      }
    } catch {
      toast.error("Erreur lors de la mise à jour", {
        description: "Une erreur est survenue lors du changement d'étape.",
      });
    } finally {
      setIsChangingOnboarding(false);
    }
  };

  const handleSignOut = async () => {
    await authClient.signOut({}, { onSuccess: () => navigate("/login") });
  };

  const handleUserDelete = async () => {
    const password = prompt("Veuillez entrer votre mot de passe pour confirmer la suppression de votre compte :") || "test1234";

    if (!activeOrg) {
      toast.error("Aucune organisation active trouvée.");
      return;
    }

    await authClient.organization.delete({ organizationId: activeOrg.id });
    await authClient.deleteUser(
      { password },
      {
        onSuccess: () => {
          toast.success("Votre compte a été supprimé avec succès.");
          navigate("/login");
        },
        onError: (error) => {
          toast.error("Une erreur est survenue lors de la suppression de votre compte.", {
            description: error.error.message,
          });
        },
      }
    );
  };

  // ============================================================================
  // NAVIGATION HELPERS
  // ============================================================================

  const isPathActive = (path: string) => {
    const [basePath, query] = path.split("?");
    if (query) {
      const tabParam = new URLSearchParams(query).get("tab");
      const currentTab = new URLSearchParams(location.search).get("tab");
      return location.pathname === basePath && currentTab === tabParam;
    }
    return location.pathname === basePath;
  };

  const isItemActive = (item: NavItem) => {
    const prefix = item.activePathPrefix || item.path;

    // Handle query params
    if (prefix.includes("?")) {
      return isPathActive(prefix);
    }

    // Exact match mode - only match the exact path
    if (item.exactMatch) {
      return location.pathname === prefix;
    }

    // Prefix matching (default)
    return location.pathname === prefix || location.pathname.startsWith(prefix + "/");
  };

  const toggleOpen = (itemId: string) => {
    setOpenStates((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const orgName = activeOrg?.name || "Votre club";
  const orgLogo = activeOrg?.logo || "https://images.nautika.app/clubs/placeholder.jpeg";
  const orgDesc = activeOrg?.metadata || "Espace club";

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div
        ref={sidebarRef}
        data-collapsed={isCollapsed}
        style={{ width: isCollapsed ? MIN_WIDTH : sidebarWidth }}
        className={cn("group relative flex flex-col border-r border-gray-200 bg-white transition-all duration-200 ease-out", isDragging && "transition-none")}
      >
        {/* Toggle button */}
        <div className="flex justify-end px-3 py-2 group-data-[collapsed=true]:justify-center">
          <button
            onClick={toggleCollapse}
            className="flex items-center justify-center rounded-md p-2 text-gray-300 transition-colors hover:cursor-pointer hover:bg-gray-100 hover:text-gray-600"
          >
            <PanelLeftClose className="block size-5 shrink-0 group-data-[collapsed=true]:hidden" />
            <PanelLeftOpen className="hidden size-5 shrink-0 group-data-[collapsed=true]:block" />
          </button>
        </div>

        {/* Organization header */}
        <div className="flex h-auto justify-center overflow-hidden px-6 pb-6 opacity-100 transition-[height,padding] duration-200 group-data-[collapsed=true]:h-0 group-data-[collapsed=true]:pb-0 group-data-[collapsed=true]:opacity-0">
          <div className="flex flex-col items-center">
            <div className="mb-2 flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-blue-100">
              <img src={orgLogo} alt={`Logo ${orgName}`} className="h-full w-full object-cover" />
            </div>
            <h2 className="text-center font-semibold text-gray-800">{orgName}</h2>
            <p className="text-sm text-gray-500">{orgDesc}</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto pt-4">
          <div className="space-y-1 px-3 group-data-[collapsed=true]:px-2">
            {navigationConfig.map((item) => {
              const isActive = isItemActive(item);
              const isOpen = openStates[item.id] && !isCollapsed && !isDragCollapsed;
              const hasChildren = item.children && item.children.length > 0;
              const IconComponent = item.icon;

              // Wrapper for collapsible items
              const ItemWrapper = hasChildren ? Collapsible : "div";
              const wrapperProps = hasChildren ? { open: isOpen, onOpenChange: () => toggleOpen(item.id) } : {};

              return (
                <ItemWrapper key={item.id} {...wrapperProps}>
                  {/* Parent row */}
                  <div
                    className={cn(
                      "group/item relative flex items-center overflow-hidden rounded-md transition-colors",
                      !item.enabled
                        ? "hidden cursor-not-allowed text-gray-400"
                        : isActive && isOpen
                          ? "text-orange-500"
                          : isActive
                            ? "bg-orange-50 text-orange-500"
                            : "text-gray-600 [&:hover:not(:has(button:hover))]:bg-gray-100"
                    )}
                  >
                    {/* Clickable navigation area (icon + label) - takes remaining space, allows truncation */}
                    <Tooltip delayDuration={isCollapsed ? 0 : 999999}>
                      <TooltipTrigger asChild>
                        <button
                          disabled={!item.enabled}
                          onClick={() => item.enabled && navigate(item.path)}
                          className={cn(
                            "flex min-w-0 flex-1 items-center px-3 py-2 text-left text-sm group-data-[collapsed=true]:justify-center group-data-[collapsed=true]:px-2",
                            item.enabled && "hover:cursor-pointer"
                          )}
                        >
                          <IconComponent className="mr-3 size-5 shrink-0 group-data-[collapsed=true]:mr-0" />
                          <span className={cn("truncate group-data-[collapsed=true]:hidden", isActive && "font-medium")}>{item.label}</span>
                        </button>
                      </TooltipTrigger>
                      {isCollapsed && (
                        <TooltipContent side="right" sideOffset={8}>
                          {item.label}
                        </TooltipContent>
                      )}
                    </Tooltip>

                    {/* Action button (e.g., +) - visible only on hover with fade */}
                    {item.action && (
                      <Tooltip delayDuration={250}>
                        <TooltipTrigger asChild>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(item.action!.path);
                            }}
                            className="z-10 shrink-0 cursor-pointer rounded-md p-2 text-gray-400 opacity-0 transition-all duration-200 group-hover/item:opacity-100 group-data-[collapsed=true]:hidden hover:bg-gray-200 hover:text-gray-600"
                          >
                            <item.action.icon className="size-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" sideOffset={4}>
                          {item.action.tooltip}
                        </TooltipContent>
                      </Tooltip>
                    )}

                    {/* Chevron collapse trigger - visible only on hover with fade */}
                    {hasChildren && (
                      <CollapsibleTrigger asChild>
                        <button
                          onClick={(e) => e.stopPropagation()}
                          className="z-10 mr-0.5 shrink-0 cursor-pointer rounded-md p-2 text-gray-400 opacity-0 transition-all duration-200 group-hover/item:opacity-100 group-data-[collapsed=true]:hidden hover:bg-gray-200 hover:text-gray-600"
                        >
                          <ChevronRight className={cn("size-4 transition-transform duration-200", isOpen && "rotate-90")} />
                        </button>
                      </CollapsibleTrigger>
                    )}
                  </div>

                  {/* Children (only rendered for collapsible items) */}
                  {hasChildren && (
                    <CollapsibleContent className="space-y-0.5 pt-1 pl-6">
                      {item.children!.map((child) => {
                        const SubIcon = child.icon;
                        const isChildActive = isPathActive(child.path);
                        return (
                          <button
                            key={child.id}
                            disabled={!child.enabled}
                            onClick={() => child.enabled && navigate(child.path)}
                            className={cn(
                              "flex w-full items-center rounded-md px-2 py-1 text-left text-xs transition-colors",
                              !child.enabled
                                ? "hidden cursor-not-allowed text-gray-400"
                                : isChildActive
                                  ? "bg-orange-50 font-medium text-orange-500"
                                  : "text-gray-500 hover:cursor-pointer hover:bg-gray-100 hover:text-gray-600"
                            )}
                          >
                            <SubIcon className="mr-2 size-3.5 shrink-0" />
                            <span className="truncate">{child.label}</span>
                          </button>
                        );
                      })}
                    </CollapsibleContent>
                  )}
                </ItemWrapper>
              );
            })}
          </div>
        </nav>

        {/* Footer section */}
        <div className="border-t border-gray-200 p-4 group-data-[collapsed=true]:border-transparent group-data-[collapsed=true]:p-2">
          {/* Dev tools - hidden when collapsed */}
          <div className="block group-data-[collapsed=true]:hidden">
            <div className="mb-4">
              <SeedPopover />
            </div>

            <div className="mb-4">
              <SentryTestButton />
            </div>

            <div className="mb-4">
              <label className="mb-2 block text-xs font-medium text-gray-700">Étape d'onboarding</label>
              <Select value={session?.user?.onboarding || "done"} onValueChange={handleOnboardingStepChange} disabled={isChangingOnboarding}>
                <SelectTrigger className="w-full text-sm font-medium">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {onboardingSteps.map((step, index) => (
                    <SelectItem key={`onboarding-step-${step.value}-${index}`} value={step.value} className="text-sm">
                      {step.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* User section with dropdown menu */}
          <DropdownMenu>
            <Tooltip delayDuration={isCollapsed ? 0 : 999999}>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <button className="flex w-full cursor-pointer items-center rounded-md p-2 text-left transition-colors group-data-[collapsed=true]:justify-center group-data-[collapsed=true]:p-1 hover:bg-gray-100">
                    <Avatar className="size-8 flex-none shrink-0 group-data-[collapsed=true]:size-8">
                      <AvatarImage src="/api/placeholder/32/32" alt="User" />
                      <AvatarFallback>{session?.user?.first_name?.charAt(0) || session?.user?.email?.charAt(0) || "U"}</AvatarFallback>
                    </Avatar>
                    <div className="ml-3 flex-1 overflow-hidden text-left group-data-[collapsed=true]:hidden">
                      <p className="truncate text-sm font-medium text-gray-900">{session?.user?.first_name || "User"}</p>
                      <p className="truncate text-xs text-gray-500">{session?.user?.email}</p>
                    </div>
                    <ChevronsUpDown className="ml-2 size-4 shrink-0 text-gray-400 group-data-[collapsed=true]:hidden" />
                  </button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              {isCollapsed && (
                <TooltipContent side="right" sideOffset={8}>
                  <div>
                    <p className="font-medium">{session?.user?.first_name || "User"}</p>
                    <p className="text-xs opacity-80">{session?.user?.email}</p>
                  </div>
                </TooltipContent>
              )}
            </Tooltip>
            <DropdownMenuContent side="top" align="start" className="w-56">
              <DropdownMenuLabel>Mon compte</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut}>
                <LogOut className="mr-2 size-4" />
                Se déconnecter
              </DropdownMenuItem>
              <DropdownMenuItem variant="destructive" onClick={handleUserDelete}>
                <Trash className="mr-2 size-4" />
                Supprimer mon compte
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Drag handle */}
        <div
          onMouseDown={handleMouseDown}
          style={{ right: "-2px" }}
          className={cn(
            "absolute top-0 z-10 h-full w-1 cursor-col-resize bg-transparent transition-colors duration-150 hover:bg-orange-100",
            isDragging && "bg-orange-200"
          )}
        />
      </div>

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <Outlet />
      </div>

      <OrgSelection
        open={showOrgSelection}
        organizations={organizations || []}
        selectedOrgId={selectedOrgId}
        onSelectedOrgChange={setSelectedOrgId}
        onConfirm={() => handleSetActiveOrganization(selectedOrgId)}
        onClose={onCloseOrgSelection}
        isLoading={isSettingActive}
      />
    </div>
  );
}
